import { MatDialog } from '@angular/material/dialog';
import { IEntity, IRemoteFileInfo } from 'interfaces-lib';
import { SignatureConfig } from 'interfaces-lib';
import { WorkflowMessage, WorkflowStatusMessage } from 'interfaces-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { FileListControllerService } from '../services/file-list.controller.service';
import { Stage, StageSet } from '../workflow-in-progress/workflow-in-progress.component';
import { ProgressReportingWorkflowTask } from './progress-reporting-workflow-task';

export enum SendStatus {
  CREATED = 'created',
  SENT = 'sent',
}

// NOTE: the message sequence for the SIMPLE_DOCX_S3_SAVE workflow is:
// SaveToEntity, RestoreWorkflowState, RetrievingMergedFile, MergeComplete, *SavedToEntity*
// they're all WorkflowStatus type message except for {type: SavedToEntity, message: {status: done}}

export class SendForSignatureTask extends ProgressReportingWorkflowTask {
  private static readonly STAGES: StageSet = {
    Started: { text: 'Sending', icon: 'autorenew' },
    SaveToEntity: { text: 'Sending the document', icon: 'upload_file' },
  };
  private readonly CLOSING_STAGE: Stage = {
    text: 'Document sent to',
    icon: 'done',
  };

  private editEnvelopeClicked = false;

  constructor(
    private key: string,
    private signatureConfig: SignatureConfig,
    private status: SendStatus,
    private entity: IEntity,
    private fileListControllerService: FileListControllerService,
    private messageNotificationService: MessageNotificationService,
    dialog: MatDialog,
    private editorFileInfo: IRemoteFileInfo | undefined,
    private outputFilename: string,
    private onEnvelopeUrl?: (envelopeUrl: string) => void
  ) {
    super('SEND_TO_SIGNATURE', 'Sending for signature', SendForSignatureTask.STAGES, dialog, { startStage: 'Started' });
    this.CLOSING_STAGE.text = `Document sent to ${signatureConfig.provider}`;
  }

  override getStartParams(): any {
    return {
      RESTORE_STATE: { key: this.key },
      USER_SIGNATURE_REQUEST: {
        provider: this.signatureConfig.provider,
        emailBody: this.signatureConfig.emailBody,
        emailSubject: this.signatureConfig.emailSubject,
        recipients: this.signatureConfig.recipients,
        documents: this.signatureConfig.documents,
        status: this.status,
      },
      EDITOR_FILE: this.editorFileInfo,
      OUTPUT_FILENAME_FINAL: this.outputFilename,
      SOURCE_APPLICATION: "DOC_CLIENT",
    };
  }

  override receive(message: WorkflowMessage): boolean {
    if (message.type === 'WorkflowStatus') {
      this.updateProgress(<WorkflowStatusMessage>message.message);
      return false;
    }

    if (message.type === 'ErrorEvent') {
      this.closeProgress();
      this.messageNotificationService.error('Failed to send to signature provider.', message.message);
      return true;
    }

    // occurs when send status is 'created' (i.e. edit in docusign)
    if (message.type === 'EnvelopeUrl') {
      const envelopeUrl = message.message.envelopeUrl;
      this.onEnvelopeUrl?.(envelopeUrl);

      this.messageNotificationService.success('Docusign envelope created.', envelopeUrl);
      const providerWindow = window.open(envelopeUrl, '_blank');
      if (!providerWindow) {
        this.messageNotificationService.error(
          'Failed to open signature provider window.',
          undefined,
          undefined,
          ErrorSeverity.CRITICAL,
        );
      }
      this.editEnvelopeClicked = true;
      return false;
    }

    if (message.type === 'SavedToEntity') {
      this.closeProgress(ProgressReportingWorkflowTask.DEFAULT_CLOSE_DELAY_MS, this.CLOSING_STAGE);
      this.fileListControllerService.refresh(this.entity);
      if (!this.editEnvelopeClicked) {
        this.messageNotificationService.success('Docusign envelope sent.');
      }
      return true;
    }
    console.debug(`Message '${message.type}' not handled.`);

    return false;
  }
}
